/**
 * Per-page contextual help: a small HelpCircle button (placed next to a page
 * title) that opens an in-context right-side slide-over explaining that page.
 *
 * The slide-over is hand-rolled on the same fixed-overlay pattern as
 * auth/ReauthModal.tsx (no Radix Dialog dependency). Esc and click-outside
 * close it. Content comes from the central registry, so the panel and the
 * /help guide always agree.
 */
import { HelpCircle, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { cn } from "../lib/cn";
import { HelpTopic } from "./components";
import { getTopic } from "./registry";

const ANIM_MS = 200;

export function HelpPanel({
  topic,
  onClose,
}: {
  topic: string;
  onClose: () => void;
}) {
  const entry = getTopic(topic);
  // Drive an interruptible CSS slide: mount hidden, flip `visible` true after
  // first paint to transition in; on close transition out, then unmount.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, ANIM_MS);
  }, [onClose]);

  // Esc to close.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex justify-end bg-black/60",
        "transition-opacity duration-200 ease-out",
        visible ? "opacity-100" : "opacity-0",
      )}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={entry ? `Help: ${entry.title}` : "Help"}
    >
      <div
        className={cn(
          "flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-slate-700 bg-slate-950 shadow-xl",
          "transition-transform duration-200 ease-out will-change-transform",
          visible ? "translate-x-0" : "translate-x-full",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
            <HelpCircle className="h-4 w-4 text-slate-400" />
            Help
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close help"
            className="rounded p-1 text-slate-400 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-4 py-4">
          {entry ? (
            <HelpTopic entry={entry} />
          ) : (
            <p className="text-sm text-slate-400">
              No help is written for this page yet.
            </p>
          )}

          <div className="mt-2 border-t border-slate-800 pt-3">
            <Link
              to={entry ? `/help#${entry.id}` : "/help"}
              onClick={onClose}
              className="text-xs text-emerald-400 hover:text-emerald-300"
            >
              Open full guide →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Drop next to a page's <h1>. `topic` is a key in the help registry
 * (e.g. "settings-vpn" or `chassis-${activeTab}`).
 */
export function HelpButton({
  topic,
  className = "",
}: {
  topic: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Help for this page"
        title="Help for this page"
        className={`inline-flex items-center rounded p-1 text-slate-400 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 hover:text-slate-100 active:scale-[0.96] ${className}`}
      >
        <HelpCircle className="h-4 w-4" />
      </button>
      {open && <HelpPanel topic={topic} onClose={() => setOpen(false)} />}
    </>
  );
}
